import { Link } from "@ui/Atoms/Link/Link";
import { IconCheck } from "@ui/Icons/IconCheck";
import { IconChevronLeft } from "@ui/Icons/IconChevronLeft";
import { IconMapPin } from "@ui/Icons/IconMapPin";
import { IconSearch } from "@ui/Icons/IconSearch";
import { useEffect } from "react";
import { FilterSections } from "./FilterSections";
import type { FilterHandlers, FilterSection } from "./types";

export type FilterSheetNearby = {
	id: string;
	name: string;
	meta: string;
	distance: string;
	href: string;
};

export type FilterCuisineOption = {
	slug: string;
	label: string;
	count: number;
	selected: boolean;
};

export interface FilterSheetTranslations {
	searchPlaceholder: string;
	nearbyLabel: string;
	showResults: (count: number) => string;
	clear: string;
	done: string;
	allCuisines: string;
}

type Props = {
	open: boolean;
	onClose: () => void;
	searchValue: string;
	onSearchChange: (value: string) => void;
	onSubmit: () => void;
	nearby: FilterSheetNearby[];
	sections: FilterSection[];
	handlers: FilterHandlers;
	resultCount: number;
	lowResults?: { message: string; widenLabel: string; onWiden: () => void };
	cuisineScreen?: {
		open: boolean;
		onClose: () => void;
		options: FilterCuisineOption[];
		onToggle: (slug: string) => void;
	};
	translations: FilterSheetTranslations;
	onClear: () => void;
};

export function FilterSheet({
	open,
	onClose,
	searchValue,
	onSearchChange,
	onSubmit,
	nearby,
	sections,
	handlers,
	resultCount,
	lowResults,
	cuisineScreen,
	translations,
	onClear,
}: Props) {
	useEffect(() => {
		if (!open) return;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, [open]);

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-[1100] flex flex-col bg-[var(--surface-default)]">
			<div className="flex items-center gap-2.5 border-b border-[var(--border-default)] px-4 pb-3 pt-[calc(12px+env(safe-area-inset-top))]">
				<button
					type="button"
					onClick={onClose}
					aria-label={translations.done}
					className="grid h-9 w-9 shrink-0 place-items-center"
				>
					<IconChevronLeft size={20} />
				</button>
				<div className="flex flex-1 items-center gap-2.5 rounded-[var(--radius-sm)] border-[1.5px] border-[var(--accent-500)] bg-[var(--surface-muted)] px-3.5 py-2.5">
					<IconSearch size={17} color="var(--content-muted)" />
					<input
						type="search"
						value={searchValue}
						onChange={(e) => onSearchChange(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") onSubmit();
						}}
						placeholder={translations.searchPlaceholder}
						aria-label={translations.searchPlaceholder}
						className="flex-1 border-0 bg-transparent text-[14px] outline-none"
					/>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto px-5 pb-28 pt-5">
				<div className="flex flex-col gap-6">
					{nearby.length > 0 && (
						<div className="flex flex-col gap-2.5">
							<div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--content-muted)]">
								{translations.nearbyLabel}
							</div>
							<div className="flex flex-col">
								{nearby.map((place) => (
									<Link
										key={place.id}
										href={place.href}
										className="flex items-center gap-3 border-t border-[var(--border-subtle)] py-3 first:border-t-0"
									>
										<span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-subtle)]">
											<IconMapPin size={15} color="var(--content-muted)" />
										</span>
										<span className="min-w-0 flex-1">
											<span className="block truncate text-[14px] font-bold">
												{place.name}
											</span>
											<span className="block text-[12px] text-[var(--content-muted)]">
												{place.meta}
											</span>
										</span>
										<span className="shrink-0 text-[12px] font-semibold text-[var(--content-faint)]">
											{place.distance}
										</span>
									</Link>
								))}
							</div>
						</div>
					)}

					<FilterSections
						sections={sections}
						handlers={handlers}
						chipSize="md"
					/>
				</div>
			</div>

			<div className="absolute inset-x-0 bottom-0 flex flex-col gap-2.5 border-t border-[var(--border-default)] bg-[var(--surface-default)] px-5 pb-[calc(22px+env(safe-area-inset-bottom))] pt-3">
				{lowResults && (
					<div className="flex items-center gap-2.5 rounded-[var(--radius-sm)] bg-[var(--surface-muted)] px-3 py-2.5">
						<span className="flex-1 text-[12.5px] font-semibold text-[var(--content-secondary)]">
							{lowResults.message}
						</span>
						<button
							type="button"
							onClick={lowResults.onWiden}
							className="shrink-0 text-[12.5px] font-extrabold"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{lowResults.widenLabel}
						</button>
					</div>
				)}
				<div className="flex items-center gap-3.5">
					<button
						type="button"
						onClick={onClear}
						className="shrink-0 text-[13.5px] font-bold text-[var(--content-muted)]"
					>
						{translations.clear}
					</button>
					<button
						type="button"
						onClick={onSubmit}
						className="flex h-12 flex-1 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] text-[14.5px] font-bold text-[var(--content-inverted)]"
					>
						{translations.showResults(resultCount)}
					</button>
				</div>
			</div>

			{cuisineScreen?.open && (
				<div className="absolute inset-0 flex flex-col bg-[var(--surface-default)]">
					<div className="flex items-center gap-1.5 border-b border-[var(--border-default)] px-4 pb-3 pt-[calc(12px+env(safe-area-inset-top))]">
						<button
							type="button"
							onClick={cuisineScreen.onClose}
							aria-label={translations.done}
							className="grid h-9 w-9 place-items-center"
						>
							<IconChevronLeft size={20} />
						</button>
						<div
							className="text-[16px] font-extrabold tracking-[-0.025em]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{translations.allCuisines}
						</div>
					</div>
					<div className="flex-1 overflow-y-auto px-5 pb-24 pt-1.5">
						{cuisineScreen.options.map((option) => (
							<button
								key={option.slug}
								type="button"
								onClick={() => cuisineScreen.onToggle(option.slug)}
								className="flex w-full items-center gap-3 border-b border-[var(--border-subtle)] py-3.5 text-left"
							>
								<span
									className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[var(--radius-sm)] border-[1.5px]"
									style={{
										background: option.selected
											? "var(--accent-500)"
											: "var(--surface-default)",
										borderColor: option.selected
											? "var(--accent-500)"
											: "var(--border-strong)",
									}}
								>
									{option.selected && (
										<IconCheck size={13} color="var(--content-inverted)" />
									)}
								</span>
								<span className="flex-1 text-[14.5px] font-semibold">
									{option.label}
								</span>
								<span className="text-[12.5px] font-semibold text-[var(--content-faint)]">
									{option.count}
								</span>
							</button>
						))}
					</div>
					<div className="absolute inset-x-0 bottom-0 border-t border-[var(--border-default)] bg-[var(--surface-default)] px-5 pb-[calc(22px+env(safe-area-inset-bottom))] pt-3">
						<button
							type="button"
							onClick={cuisineScreen.onClose}
							className="flex h-12 w-full items-center justify-center rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] text-[14.5px] font-bold text-[var(--content-inverted)]"
						>
							{translations.done}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
